import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit"
import {
  getAstrositeById,
  getAstrosites,
  getAstrositesState,
  getAstrositeSunInfo,
  setAstrositeAutoActivation,
} from "./api"
import { DateTime } from "luxon"
import { allowUpdate } from "../../app/store"

const initialState = {
  byId: {}, // A map of Astrosites by id
  matchesByCtx: {
    // root context powering the Astrosites view
    root: {
      _loadStatus: "idle", // Information about the loading status of a call
      _errorMessage: undefined,
      ids: [],
      page: {}, // Pagination information
    },
  },
  states: {
    byId: {},
    latestByAstrosite: {},
    matchesByCtx: {
      // root context powering the states
      root: {
        _loadStatus: "idle", // Information about the loading status of a call
        _errorMessage: undefined,
        ids: [], // Astrosite state ids
        page: {}, // Pagination information
      },
    },
  },
  sunInfoByAstrosite: {},
}

/**
 * Thunk to perform async logic to fetch Astrosites states
 * @type {AsyncThunk<*, void, {}>}
 */
export const fetchAstrositeStates = createAsyncThunk(
  "astrosites/states/fetch",
  async ({ filter, page, context }) => {
    const response = await getAstrositesState({
      filter,
      page,
    })
    if (context) {
      response.context = context
    }
    return response
  }
)

/**
 * Thunk to perform async logic to fetch latest state for an Astrosite
 * @type {AsyncThunk<*, void, {}>}
 */
export const fetchAstrositeSunInfo = createAsyncThunk(
  "astrosite/suninfo/fetch",
  async (astrositeId) => {
    const response = await getAstrositeSunInfo(astrositeId)
    return response
  }
)

/**
 * Thunk to perform async logic to fetch the Astrosites matching the provided filters
 *
 * @type {AsyncThunk<*, {readonly filter?: *, readonly context?: *, readonly page?: *}, {}>}
 */
export const fetchAstrosites = createAsyncThunk(
  "astrosites/fetch",
  /**
   *
   * @param filter the filters to apply to the search
   * @param page Pagination information
   * @param context The context from where the query is being made.
   * @returns {Promise<*>}
   */
  async ({ filter, page, context }) => {
    const response = await getAstrosites({
      filter,
      page,
    })
    if (context) {
      response.context = context
    }
    return response
  }
)

/**
 * Thunk to perform async logic to fetch an Astrosite by its ID
 * @type {AsyncThunk<*, void, {}>}
 */
export const fetchAstrositeById = createAsyncThunk(
  "astrosite/fetch",
  async (id) => {
    const response = await getAstrositeById(id)
    return response
  }
)

/**
 * Thunk to perform async logic to set the auto activation flag for an Astrosite
 * @type {AsyncThunk<*, void, {}>}
 */
export const updateAstrositeAutoActivation = createAsyncThunk(
  "astrosite/update/activation",
  async ({ id, autoActivate, _etag }, { rejectWithValue }) => {
    try {
      return await setAstrositeAutoActivation({ id, autoActivate, _etag })
    } catch (error) {
      throw rejectWithValue(error.message)
    }
  }
)

export const astrositesSlice = createSlice({
  name: "astrosites",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {},
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(fetchAstrositeStates.pending, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (!state.states.matchesByCtx[context]) {
          state.states.matchesByCtx[context] = {
            ids: [],
            page: {},
          }
        }
        state.states.matchesByCtx[context]._loadStatus = "loading"
      })
      .addCase(fetchAstrositeStates.fulfilled, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (state?.states?.matchesByCtx[context]) {
          state.states.matchesByCtx[context]._loadStatus = "idle"
          // Dont reset if we are paging?
          if (action?.payload?.page?.start_index == 0) {
            state.states.matchesByCtx[context].ids = []
          }
          if (
            allowUpdate(
              state.states.matchesByCtx[context].at,
              action.payload.at
            )
          ) {
            action?.payload?.data?.map((astroState) => {
              state.states.byId[astroState.id] = astroState
              state.states.byId[astroState.id]._at = action.payload.at
              // add the ids of the states that match from this query
              state.states.matchesByCtx[context].page = action.payload.page
              if (
                state.states.matchesByCtx[context].ids.indexOf(
                  astroState.id
                ) === -1
              ) {
                state.states.matchesByCtx[context].ids.push(astroState.id)
              }

              // We check the latest astrosite update and overwrite if we have new details
              let newDate = DateTime.fromISO(astroState.timestamp)
              let lastUpdated = !state.byId[astroState.astrosite].lastUpdated
                ? DateTime.fromISO(astroState.timestamp)
                : DateTime.fromISO(state.byId[astroState.astrosite].lastUpdated)
              if (newDate >= lastUpdated) {
                state.byId[astroState.astrosite].location = astroState.geodetic
                state.byId[astroState.astrosite].lastUpdated = newDate.toISO()
                state.byId[astroState.astrosite].deploy_state =
                  astroState.deploy_state
                state.byId[astroState.astrosite].weather_override =
                  astroState.weather_override
                state.byId[astroState.astrosite].power = astroState.power
              }
            })
            state.states.matchesByCtx[context].page = action.payload.page
            state.states.matchesByCtx[context].at = action.payload.at
          }
        }
      })
      .addCase(fetchAstrositeStates.rejected, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        state.states.matchesByCtx[context]._loadStatus = "rejected"
        state.states.matchesByCtx[context]._errorMessage =
          action?.error?.message
      })
      .addCase(fetchAstrositeSunInfo.pending, (state, action) => {
        if (!state.sunInfoByAstrosite[action.meta.arg]) {
          state.sunInfoByAstrosite[action.meta.arg] = {}
        }
        state.sunInfoByAstrosite[action.meta.arg]._loadStatus = "loading"
      })
      .addCase(fetchAstrositeSunInfo.fulfilled, (state, action) => {
        state.sunInfoByAstrosite[action?.payload?.astrosite_id] = action.payload
        state.sunInfoByAstrosite[action?.payload?.astrosite_id]._loadStatus =
          "idle"
      })
      .addCase(fetchAstrositeSunInfo.rejected, (state, action) => {
        state.sunInfoByAstrosite[action.meta.arg] = {
          ...action?.payload,
          _loadStatus: "rejected",
        }
      })
      .addCase(fetchAstrosites.pending, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (!state.matchesByCtx[context]) {
          state.matchesByCtx[context] = {
            ids: [],
            page: {},
          }
        }
        state.matchesByCtx[context]._loadStatus = "loading"
      })
      .addCase(fetchAstrosites.fulfilled, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (state.matchesByCtx[context]) {
          state.matchesByCtx[context]._loadStatus = "idle"

          if (allowUpdate(state.matchesByCtx[context].at, action.payload.at)) {
            // Don't reset if we are paging
            if (action?.payload?.page?.start_index === 0) {
              state.matchesByCtx[context].ids = []
            }
            action?.payload?.data?.map((act) => {
              // Add the Astrosite to state by id (this can include ids not retrieved in this call)
              state.byId[act.id] = {
                ...act,
                _at: action.payload.at,
              }
              // add the ids that match from this query
              if (state.matchesByCtx[context].ids.indexOf(act.id) === -1) {
                state.matchesByCtx[context].ids.push(act.id)
              }
            })
            state.matchesByCtx[context].page = action.payload.page
            state.matchesByCtx[context].at = action.payload.at
          }
        }
      })
      .addCase(fetchAstrosites.rejected, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        state.matchesByCtx[context]._loadStatus = "rejected"
        state.matchesByCtx[context]._errorMessage = action?.error?.message
      })
      .addCase(fetchAstrositeById.pending, (state, action) => {
        if (!state.byId[action.meta.arg]) {
          state.byId[action.meta.arg] = {}
        }
        state.byId[action.meta.arg]._loadStatus = "loading"
      })
      .addCase(fetchAstrositeById.fulfilled, (state, action) => {
        if (action?.payload?.id) {
          if (
            allowUpdate(
              state.byId[action.payload.id]
                ? state.byId[action.payload.id]._at
                : undefined,
              action.payload._at
            )
          ) {
            state.byId[action.payload.id] = {
              ...action.payload,
              _at: action.payload._at,
            }
          }

          state.byId[action.payload.id]._loadStatus = "idle"
        }
      })
      .addCase(fetchAstrositeById.rejected, (state, action) => {
        state.byId[action.meta.arg]._loadStatus = "rejected"
        state.byId[action.meta.arg]._errorMessage += action.payload
      })
      .addCase(updateAstrositeAutoActivation.pending, (state, action) => {
        if (!state.byId[action.meta.arg?.id]) {
          state.byId[action.meta.arg?.id] = {}
        }
        state.byId[action.meta.arg?.id]._saveStatus = "updating"
      })
      .addCase(updateAstrositeAutoActivation.fulfilled, (state, action) => {
        if (action?.payload?.id) {
          state.byId[action.payload.id].auto_activate =
            action.payload.auto_activate
          state.byId[action.payload.id]._saveStatus = "idle"
        }
      })
      .addCase(updateAstrositeAutoActivation.rejected, (state, action) => {
        if (!state.byId[action.meta.arg?.id]) {
          state.byId[action.meta.arg?.id] = {}
        }
        state.byId[action.meta.arg?.id]._saveStatus = "rejected"
        state.byId[action.meta.arg?.id]._errorMessage += action.payload
      })
  },
})

export const selectAstrositesById = (state) => state.astrosites.byId
export const selectAstrositesStatesById = (state) =>
  state.astrosites.states.byId

export const selectAstrositesAllSkyById = (state) =>
  state.astrosites.allSky.byId

/**
 * Takes in a context parameter and returns matches from matchesByContext[context]
 * Usage in components: const items = useSelector((state) => selectAstrositeStatesByContext(state, `rso/${id}`))
 */
export const selectAstrositeStatesByContext = createSelector(
  [
    // first input - extract value from `state`
    (state) => state.astrosites.states.matchesByCtx,
    // Take the second arg, `context`, and forward to the output selector
    (state, context) => context,
  ],
  // Output selector gets (`states, context)` as args
  (matchesByCtx, context) => {
    return matchesByCtx[context]
  }
)

/**
 * Takes in a list of ids as a parameter and returns matches from byIds
 * Usage in components: const items = useSelector((state) => selectAstrositeStatesByIds(state, ["1", "2"]))
 */
export const selectAstrositeStatesByIds = createSelector(
  [
    // first input - extract value from `state`
    selectAstrositesStatesById,
    // Take the second arg, `ids`, and forward to the output selector
    (state, ids) => ids,
  ],
  // Output selector gets (`byId, ids)` as args
  (byId, ids) => {
    return ids.map((id) => byId[id] || { id })
  }
)

/**
 * Takes in an astrosite idas a parameter and returns the relevant asatrosite from state
 * Usage in components: const items = useSelector((state) => selectAstrositeById(state,"astrosite2"))
 */
// export const selectAstrositeById = createSelector(
//   [(state) => state.astrosites.byId, (state, id) => id],
//   (byId, id) => {
//     return byId[id]
//   }
// )

/**
 * Takes in a context parameter and returns matches from matchesByContext[context]
 * Usage in components: const items = useSelector((state) => selectAstrositesByContext(state, `rso/${id}`))
 */
export const selectAstrositesByContext = createSelector(
  [
    // first input - extract value from `state`
    (state) => state.astrosites.matchesByCtx,
    // Take the second arg, `context`, and forward to the output selector
    (state, context) => context,
  ],
  // Output selector gets (`astrosites, context)` as args
  (matchesByCtx, context) => {
    return matchesByCtx[context]
  }
)

/**
 * Takes in a list of ids as a parameter and returns matches from byIds
 * Usage in components: const items = useSelector((state) => selectAstrositeByIds(state, ["1", "2"]))
 */
export const selectAstrositeByIds = createSelector(
  [
    // first input - extract value from `state`
    selectAstrositesById,
    // Take the second arg, `ids`, and forward to the output selector
    (state, ids) => ids,
  ],
  // Output selector gets (`byId, ids)` as args
  (byId, ids) => {
    return ids.map((id) => byId[id] || { id })
  }
)

/**
 * Takes in a context parameter and returns matches from matchesByContext[context]
 * Usage in components: const items = useSelector((state) => selectAstrositeAllSkysByContext(state, `rso/${id}`))
 */
export const selectAstrositeAllSkysByContext = createSelector(
  [
    // first input - extract value from `state`
    (state) => state.astrosites.allSky.matchesByCtx,
    // Take the second arg, `context`, and forward to the output selector
    (state, context) => context,
  ],
  // Output selector gets (`states, context)` as args
  (matchesByCtx, context) => {
    return matchesByCtx[context]
  }
)

/**
 * Takes in a list of ids as a parameter and returns matches from byIds
 * Usage in components: const items = useSelector((state) => selectAstrositeStatesByIds(state, ["1", "2"]))
 */
export const selectAstrositeAllSkysByIds = createSelector(
  [selectAstrositesAllSkyById, (state, ids) => ids],
  // Output selector gets (`byId, ids)` as args
  (byId, ids) => {
    return ids.map((id) => byId[id] || { id })
  }
)

export const selectSunInfoByAstrositeId = createSelector(
  [(state) => state.astrosites.sunInfoByAstrosite, (state, id) => id],
  (sunInfoByAstrosite, id) => {
    return sunInfoByAstrosite[id]
  }
)

export default astrositesSlice.reducer
